"use client";

import { usePathname, useSearchParams } from "next/navigation";
import {
  Suspense,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSessionStorage } from "usehooks-ts";
import { Action, useButtonAnalytics } from "./AnalyticsProvider";

export type WidgetTab = "Send" | "Receive" | "Swap" | "Bridge" | "Buy";

export type OpenWidgetTabFrom = "RightNav" | "Balances";

export type NavTab = "quests" | "balances" | "inventory";

export interface OpenWidgetParams {
  tab: WidgetTab;
  from: OpenWidgetTabFrom;
  targetTokenAddress?: string;
}

interface RightNavContextType {
  isCollapsed: boolean;
  isInitialLoad: boolean;
  toggleCollapse: () => void;
  widgetConfig: OpenWidgetParams | null;
  openWidgetTab: (params: OpenWidgetParams) => void;
  closeWidgetTab: () => void;
  selectedWallet: string;
  setSelectedWallet: (wallet: string) => void;
  isWidgetOpen: boolean;
  activeTab: NavTab;
  setActiveTab: (tab: NavTab) => void;
}

const COLLAPSED_BY_DEFAULT_PAGES = ["/balance"];
const WIDGET_CONFIG_KEY = "rightNavWidgetConfig";

function getDefaultCollapsedState(pathname: string | null): boolean {
  if (pathname) {
    return COLLAPSED_BY_DEFAULT_PAGES.some((page) => pathname.startsWith(page));
  }
  return false; // Expanded by default
}

function getSavedCollapsedState(pathname: string | null): boolean {
  // SSR defaults to collapsed (mobile-first, prevents flash on mobile)
  if (typeof window === "undefined") {
    return false; // SSR defaults to expanded (CSS handles mobile hiding)
  }

  // Fall back to default behavior if no saved state
  return getDefaultCollapsedState(pathname);
}

const RightNavContext = createContext<RightNavContextType | undefined>(
  undefined,
);

// Map URL query param values to NavTab values
const NAV_TAB_MAP: Record<string, NavTab> = {
  rewards: "quests",
  quests: "quests",
  balances: "balances",
  inventory: "inventory",
};

// Internal component that handles search params - wrapped in Suspense
function NavTabParamHandler({
  setActiveTab,
  setIsCollapsed,
}: {
  setActiveTab: (tab: NavTab) => void;
  setIsCollapsed: (collapsed: boolean) => void;
}) {
  const searchParams = useSearchParams();

  // Handle navTab query parameter to open specific tabs
  useEffect(() => {
    const navTabParam = searchParams?.get("navTab");
    if (navTabParam) {
      const mappedTab = NAV_TAB_MAP[navTabParam.toLowerCase()];
      if (mappedTab) {
        setActiveTab(mappedTab);
        // Always expand the nav when redirected with navTab param
        setIsCollapsed(false);
      }
    }
  }, [searchParams, setActiveTab, setIsCollapsed]);

  return null;
}

export function RightNavProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [isCollapsed, setIsCollapsed] = useState(() =>
    getSavedCollapsedState(pathname),
  );
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [widgetConfig, setWidgetConfig] =
    useSessionStorage<OpenWidgetParams | null>(WIDGET_CONFIG_KEY, null);
  const [selectedWallet, setSelectedWallet] = useState<string>("");
  const [activeTab, setActiveTab] = useState<NavTab>("quests");
  const { track } = useButtonAnalytics();

  useEffect(() => {
    if (!isCollapsed)
      document.body.classList.add(...["overflow-hidden", "md:overflow-auto"]);
    else
      document.body.classList.remove(
        ...["overflow-hidden", "md:overflow-auto"],
      );
  }, [isCollapsed]);

  const toggleCollapse = () => {
    // Enable animations after first user interaction
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
    setIsCollapsed(!isCollapsed);
  };

  const openWidgetTab = (params: OpenWidgetParams) => {
    // Enable animations after first user interaction
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }

    // Ensure nav is expanded when opening tab
    if (isCollapsed) {
      setIsCollapsed(false);
    }

    setWidgetConfig(params);
  };

  const closeWidgetTab = () => {
    setWidgetConfig(null);

    track({
      action: Action.PRESSED,
      extras: {
        id: "closeWidgetTab",
        type: "cta",
        label: "Close widget tab",
        section: "RightNav",
      },
    });
  };

  return (
    <RightNavContext.Provider
      value={{
        isCollapsed,
        isInitialLoad,
        toggleCollapse,
        widgetConfig,
        openWidgetTab,
        closeWidgetTab,
        selectedWallet,
        setSelectedWallet,
        isWidgetOpen: widgetConfig !== null,
        activeTab,
        setActiveTab,
      }}
    >
      <Suspense fallback={null}>
        <NavTabParamHandler
          setActiveTab={setActiveTab}
          setIsCollapsed={setIsCollapsed}
        />
      </Suspense>
      {children}
    </RightNavContext.Provider>
  );
}

export function useRightNav() {
  const context = useContext(RightNavContext);
  if (context === undefined) {
    throw new Error("useRightNav must be used within a RightNavProvider");
  }
  return context;
}
